import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Spinner, Card, Table, Image } from 'react-bootstrap';
import axios from 'axios'; // Import axios for API requests

const BASE_URL = 'https://drdprints.com/api'; // Define the base URL for your API

const CategoryManager = () => {
  const [catTitle, setCatTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState('success');
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/get-category`);
        console.log(response.data); // Log the data to inspect the structure
        setCategories(response.data.categories || []);
      } catch (error) {
        setMessage('Error fetching categories. Please try again.');
        setVariant('danger');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleAddCategory = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('cat_title', catTitle);
      if (image) {
        formData.append('image', image);
      }

      const response = await axios.post(`${BASE_URL}/store-category`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      setMessage('Category added successfully!');
      setVariant('success');
      setCategories([...categories, result]);
      setCatTitle('');
      setImage(null);
      setImagePreview(null);
    } catch (error) {
      setMessage('Error adding category. Please try again.');
      setVariant('danger');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('cat_title', catTitle);
      if (image) {
        formData.append('image', image);
      }
      formData.append('_method', 'PUT')

      const category = categories.find((cat) => cat.id === categoryId); // Find the category by ID
      const response = await axios.post(`${BASE_URL}/update-category/${category.id}`, formData);

      const result = response.data;
      setMessage('Category updated successfully!');
      setVariant('success');
      setCategories(categories.map((cat) => (cat.id === categoryId ? result : cat)));
      setCatTitle('');
      setCategoryId('');
      setImage(null);
      setImagePreview(null);
    } catch (error) {
      setMessage('Error updating category. Please try again.');
      setVariant('danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    setLoading(true);
    try {
      const category = categories.find((cat) => cat.id === id); 
      await axios.delete(`https://drdprints.com/api/deletecategory/${category.id}`);
      setMessage('Category deleted successfully!');
      setVariant('success');
      setCategories(categories.filter((cat) => cat.id !== id));
    } catch (error) {
      setMessage('Error deleting category. Please try again.');
      setVariant('danger');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (category) => {
    setCategoryId(category.id);
    setCatTitle(category.cat_title);
    if (category.cat_image) {
      setImagePreview(`http://drdprints.com/uploads/category/images/${category.cat_image}`);
    } else {
      setImagePreview(null);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-md-center">
        <Col md={10}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title className="text-center">Manage Categories</Card.Title>
              {message && <Alert variant={variant} className="text-center">{message}</Alert>}

              {/* Add Category Form */}
              <Form className="mb-4">
                <Form.Group controlId="catTitle" className="mb-3">
                  <Form.Label>Category Title</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter category title"
                    value={catTitle}
                    onChange={(e) => setCatTitle(e.target.value)}
                    className="rounded-pill"
                  />
                </Form.Group>

                <Form.Group controlId="image" className="mb-3">
                  <Form.Label>Image</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    className="rounded-pill"
                  />
                </Form.Group>

                {imagePreview && (
                  <div className="position-relative mb-3">
                    <Image src={imagePreview} alt="Selected Image" thumbnail fluid />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0 m-2 rounded-circle"
                      onClick={handleRemoveImage}
                    >
                      &times;
                    </Button>
                  </div>
                )}

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={categoryId ? handleUpdateCategory : handleAddCategory}
                    className="rounded-pill"
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : (categoryId ? 'Update Category' : 'Add Category')}
                  </Button>
                </div>
              </Form>

              {/* Categories Table */}
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Image</th>
                    <th>Category Title</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td>{category.id}</td>
                      <td>
                        {category.cat_image ? (
                          <img
                            src={`http://drdprints.com/uploads/category/images/${category.cat_image}`}
                            alt={category.cat_title}
                            style={{ width: '50px', height: '50px' }}
                          />
                        ) : (
                          'No Image'
                        )}
                      </td>
                      <td>{category.cat_title}</td>
                      <td>{category.created_at ? new Date(category.created_at).toLocaleString() : 'N/A'}</td>
                      <td className="gap-2 d-flex flex-column">
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditClick(category)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CategoryManager;
